import { useParams } from "react-router-dom";
import { useState, useRef, useEffect, useCallback, useMemo } from "react";

import { 
  Breadcrumbs, Box, Button, 
  Collapse,
  FormControl, FormControlLabel,
  IconButton, InputAdornment,
  OutlinedInput,
  Typography, TextField, Tabs, Tab,
  Switch,
  useTheme 
} from "@mui/material";

import styled from "styled-components";

import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';

import { 
  useGetTableData, useGetTableDataInQueryString,
  useCreateTableData, 
  usePutTableData,
  useDeleteTableData, useDeleteTableDataNew,
} from "../../../components/Table/hooks";

import { standardScenarioColumns } from "../../../constants/columns";

import { NewTableTemplate } from "../../../components/Table/TableTemplate";
import { ModalTemplate } from "../../../components/Table/ModalTemplate";
import Buttons from "../../../../common/Buttons";
import * as message from "../../../constants/messages";
import { 
  BreadcrumbsTable,
  ConfirmDialog,
  deepCopy,
  SnackbarAlert,
} from "../../../components/common/HelperFunc";
import { FONT_PROPS, COLORS } from "../../../styles/colors";

///////////////////////////////
// 스타일
///////////////////////////////

// 테이블 컨테이너 스타일
const TableContainer = styled.div`
  width: 100%;
  height: 100%;
  padding: 20px 32px 20px 32px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

///////////////////////////////
// 비상사태 시나리오 컴포넌트
///////////////////////////////
const PARENT_TITLE = "정보관리";
const CHILD_TITLE = "비상사태 시나리오";
export function StandardScenario() {
  const theme = useTheme();
  //const { group_code, project_code } = useParams();

  // 탭 상태관리
  const [tabValue, setTabValue] = useState('one');
  const handleTabChange = (event, newValue) => { setTabValue(newValue); };

  ////////////////////////
  // 렌더링
  ////////////////////////
  function renderData() {
    return (
      <>
        {/* 탭 기능 */}
        <Box sx={{ display: 'flex', borderBottom: `1px solid ${theme.palette.divider}`,}}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label="basic tabs example"
            sx={{
              borderBottom: 'none',
              marginLeft: '32px',
              '& .MuiTabs-indicator': {
                backgroundColor: theme.palette.primary.main,
              },
            }}
          >
            <Tab value="one" label="화재 시나리오" 
              sx={{
                borderLeft: `1px solid ${theme.palette.divider}`,
                borderRight: `1px solid ${theme.palette.divider}`,
                borderTop: `1px solid ${theme.palette.divider}`,
                borderTopLeftRadius: '0.375rem',
                borderTopRightRadius: '0.375rem',
                marginRight: '0.3rem',
                bgcolor: tabValue === 'one' ? 'rgb(var(--mui-palette-primary-mainChannel) / 0.16)' : 'var(--mui-palette-background-paper)',
                fontFamily: FONT_PROPS.PRIMARY,
                fontWeight: FONT_PROPS.MEDIUM,
                fontSize: '16px',
              }}
            />
            <Tab value="two" label="안전사고 시나리오"
              sx={{
                borderLeft: `1px solid ${theme.palette.divider}`,
                borderRight: `1px solid ${theme.palette.divider}`,
                borderTop: `1px solid ${theme.palette.divider}`,
                borderTopLeftRadius: '0.375rem',
                borderTopRightRadius: '0.375rem',
                marginRight: '0.3rem',
                bgcolor: tabValue === 'two' ? 'rgb(var(--mui-palette-primary-mainChannel) / 0.16)' : 'var(--mui-palette-background-paper)',
                fontFamily: FONT_PROPS.PRIMARY,
                fontWeight: FONT_PROPS.MEDIUM,
                fontSize: '16px',
              }}
            />
          </Tabs>
        </Box>

        {/* 화재 시나리오 */}
        {tabValue === 'one' && 
          <FireScenario />
        }
        {/* 안전사고 시나리오 */}
        {tabValue === 'two' && 
          <WorkplaceAccidentScenario />
        }
      </>
    );
  }

  // StandardScenario 렌더링
  return (
    <Box sx={{ bgcolor: 'var(--mui-palette-background-paper)',height: '100%',display: 'flex',flexDirection: 'column'}}>
      {/* 브레드크럼 + 타이틀 */}
      <BreadcrumbsTable parentTitle={PARENT_TITLE} childTitle={CHILD_TITLE} useTab={true} />
      {/* 테이블 표시 */}
      {renderData()}
    </Box>
  );
}

// 화재 시나리오 컴포넌트
const FireScenario = () => {
  const theme = useTheme();
  const { group_code, project_code } = useParams();

  const SIT_CATEGORY = 1; // 상황카테고리 : 1(화재)
  const UPDATE_STATUS = Object.freeze({ // 수정 상태
    EDIT: 'edit',
    ROW_ORDERING: 'rowOrdering',
  });

  ////////////////////////
  // CRUD 요청
  ////////////////////////

  // 데이터 조회 요청
  const getData = useGetTableDataInQueryString(
    "request",
    "/getSafeFireScenario",
    group_code,
    project_code,
    { params: { sitCategory: SIT_CATEGORY } } // 쿼리스트링으로 상황카테고리 전달
  );
  // 데이터 생성 요청
  const postData = useCreateTableData( 
    "request",
    "/postSafeFireScenario",
    group_code,
    project_code
  );
  // 데이터 수정 요청
  const putData = usePutTableData( 
    "request",
    "/putSafeFireScenario",
    group_code,
    project_code
  );
  // 데이터 삭제 요청
  const deleteData = useDeleteTableDataNew(
    "request",
    "/deleteSafeFireScenario",
    group_code,
    project_code
  );

  const tempGetData = getData.data || [];

  // 데이터 업데이트 알람 상태 관리
  const [dataAlert, setDataAlert] = useState({
    isOpen: false,
    message: '',
    severity:'' // 'success' | 'error' | 'warning' | 'info'
  });
  const openDataAlert = (message, severity = 'success') => {
    setDataAlert({
      ...dataAlert,
      isOpen: true,
      message: message,
      severity: severity
    });
  };
  const closeDataAlert = () => {
    setDataAlert({
      ...dataAlert,
      isOpen: false,
      message: '',
      severity: '',
    });
  };

  // 테이블 참조
  const tableRef = useRef();
  const clearSelection = () => { tableRef.current?.clearSelection(); };
  const getSelectedRows = () => { return tableRef.current?.getSelectedRows(); };

  // 테이블의 상단 툴바 커스터마이징 관련 내용
  const [showSearch, setShowSearch] = useState(false); // 검색창 열림 여부
  const [globalFilter, setGlobalFilter] = useState(''); // 검색 텍스트 상태
  const customTableTopToolbar = (table) => {
    return(
      <Box width="100%" display="flex" justifyContent="space-between" alignItems="center" p={2}>
        {/* 왼쪽 영역 */}
        <Box display="flex" alignItems="center" gap={2}>
          {/* TODO: */}
        </Box>
        
        {/* 오른쪽 영역 - 기존 내부 액션도 원하면 여기에 직접 렌더링 */}
        <Box display="flex" alignItems="center" gap={2}>
          {/* 검색창 */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton onClick={() => setShowSearch((prev) => !prev)}>
              <SearchIcon />
            </IconButton>
        
            <Collapse in={showSearch} orientation="horizontal">
              <TextField
                size="small"
                placeholder="검색"
                value={globalFilter}
                onChange={(e) => {
                  setGlobalFilter(e.target.value);
                  table.setGlobalFilter(e.target.value);
                }}
                sx={{ minWidth: 200 }}
                variant="outlined"
                slots={{ input: OutlinedInput }}
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => {
                            setGlobalFilter('');
                            table.setGlobalFilter('');
                            setShowSearch(false); // 닫기
                          }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                    </InputAdornment>
                    ),
                  }
                }}
              />
            </Collapse>
          </Box>
          {/* 추가 버튼 */}
          <Buttons.Add
            onClick={() => openAddModal()}
            sx={{ padding: '12px 16px' }}
          />
          {/* 삭제 버튼 */}
          <Buttons.Delete 
            onClick={openDeleteRecordModal}
            disabled={table.getSelectedRowModel().rows.length === 0}
            sx={{ padding: '12px 16px' }}
          />    
        </Box>
      </Box>
    );
  };

  // 추가 모달 상태 관리
  const [addModal, setAddModal] = useState({ isOpen: false });
  const openAddModal = () =>  setAddModal({ isOpen: true });
  const closeAddModal = () => setAddModal({ isOpen: false });
  // DB 업데이트(추가 데이터)
  const updateAddData = async (addData) => {
    try {
      //console.log("새로운 데이터: ", addData);

      // 상황카테고리 추가
      addData.sit_category = SIT_CATEGORY;

      // 데이터베이스 업데이트
      const result = await postData.mutateAsync(addData);
      if(result.status === 200) {
        openDataAlert(message.addSuccess);
      }

      // 생성 모달 초기화
      closeAddModal();
    }
    catch (error) {
      console.error("데이터 추가 중 오류 발생:", error);
    }
  };

  // 수정 모달 상태 관리
  const [editModal, setEditModal] = useState({ isOpen: false, rowData: [] });
  const openEditModal = (rowData) => setEditModal({ isOpen: true, rowData: rowData });
  const closeEditModal = () => setEditModal({ isOpen: false, rowData: []});
  // 테이블 행 수정 액션
  const customRenderRowActionContent = (rowData) => {
    return(
      <Buttons.Edit
        onClick={(event) =>{
          event.stopPropagation(); // 이벤트 전파 중단
          openEditModal(rowData);
        }}
        sx={{ padding: '8px 12px' }}
      />
    );
  };
  // DB 업데이트 (수정 데이터)
  const updateEditData = async (updateData) => {
    try {
      const data ={
        updateData: updateData,
        updateStatus: UPDATE_STATUS.EDIT, 
      };
      const result = await putData.mutateAsync(data);
      if(result.status === 200) {
        openDataAlert(message.updateEditSuccess);
      }

      // 수정 모달 초기화
      closeEditModal();
    }
    catch (error) {
      console.error("데이터 수정 중 오류 발생:", error);
    }
  }
  // DB 업데이트 (행 순서 변경)
  const updateRowOrderingData = async (draggedRowData, hoveredRowData) =>{
    try {
      //console.log('드래그한 row의 데이터:', draggedRowData);
      //console.log('호버된 row의 데이터:', hoveredRowData);

      const data = {
        updateData: {
          dragged_id: draggedRowData.id,
          dragged_sit_index: draggedRowData.sit_index,
          hovered_id: hoveredRowData.id,
          hovered_sit_index: hoveredRowData.sit_index,
          sit_category: SIT_CATEGORY,
        },
        updateStatus: UPDATE_STATUS.ROW_ORDERING,
      };

      // 데이터베이스 업데이트
      const result = await putData.mutateAsync(data);
      if(result.status) {
        openDataAlert(message.updateEditSuccess);
      }
    }
    catch (error) {
      console.error("데이터 수정 중 오류 발생:", error);
    }
  };

  // 레코드 삭제 모달 상태 관리
  const [deleteRecordModal, setDeleteRecordModal] = useState({ isOpen: false });
  const openDeleteRecordModal = () => setDeleteRecordModal({ isOpen: true });
  const closeDeleteRecordModal = () => setDeleteRecordModal({ isOpen: false });
  // DB 업데이트 (레코드 데이터 삭제) 
  const deleteRecordData = async () => {
    closeDeleteRecordModal();
    //console.log('✅ 확인 눌림 → 다음 동작 실행');
    try {

      // 선택된 행(row) 가져오기
      const selectedRows = getSelectedRows().map((row) => row.original);

      // 데이터베이스 업데이트
      const result = await deleteData.mutateAsync({
        params: {},
        body: {
          deleteData: selectedRows,
          sitCategory: SIT_CATEGORY,
        },
      });

      if(result.status === 200) {
        openDataAlert(message.deleteSuccess);
      }

      // 선택 상태 초기화
      clearSelection();
    }
    catch (error) {
      console.error("데이터 삭제 중 오류 발생:", error);
    }
  };

  return (
    <TableContainer>
      {/** 테이블 표시 */}
      <NewTableTemplate
        ref={tableRef}
        columns={standardScenarioColumns}
        data={tempGetData}
        topToolbarContent={customTableTopToolbar}
        renderRowActionContent={customRenderRowActionContent}
        onRowOrdering={updateRowOrderingData}
        enableCustomGlobalFilter={true}
        enableRowOrdering={true}
        templateSX ={{
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: "0.375rem", 
          boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.05)',
        }}
      />
      {/* 행(row) 추가 모달 */}
      <FireCreateRowModal
        isOpen={addModal.isOpen}
        onConfirm={updateAddData}
        onCancel={closeAddModal}
      />
      {/* 행(row) 수정 모달 */}
      <FireEditRowModal
        isOpen={editModal.isOpen}
        originalRowData={editModal.rowData}
        onConfirm={updateEditData}
        onCancel={closeEditModal}
      />
      {/** 레코드 삭제 메시지 */}
      <ConfirmDialog
        open={deleteRecordModal.isOpen}
        title="정말 삭제하시겠어요?"
        message="삭제하면 복구할 수 없습니다."
        onConfirm={deleteRecordData}
        onCancel={closeDeleteRecordModal}
      />
      {/* 데이터 알람 */}
      <SnackbarAlert 
        open={dataAlert.isOpen}
        onClose={closeDataAlert}
        message={dataAlert.message}
        severity={dataAlert.severity} // 'success' | 'error' | 'warning' | 'info'
        //anchorOrigin = {{vertical: "top", horizontal: "center"}}
      />
    </TableContainer>
  );
};
// 화재 시나리오 테이블의 행(row) 추가 모달
const FireCreateRowModal = ({ isOpen, onConfirm, onCancel }) => {
  const theme = useTheme();

  // 유효성 검사 알람 상태 관리
  const [validationAlert, setValidationAlert] = useState({
    isOpen: false,
    message: '',
    severity: '',
  });
  const openValidationAlert = (message, severity = 'error') => {
    setValidationAlert({
      ...validationAlert,
      isOpen: true,
      message: message,
      severity: severity,
    });
  };
  const closeValidationAlert = (event, reason) => {
    if (reason === 'clickaway') return;
    setValidationAlert({
      ...validationAlert,
      isOpen: false,
      message: '',
      severity: '',
    });
  };

  // 추가 행 데이터 관리
  const [addData, setAddData] = useState([]);
  const clearAddData = () => setAddData([]);
  // 행 추가 완료 액션
  const addAction = () => {
    if(checkEmptyData(addData)) {
      openValidationAlert(message.notAllowedEmpty);
      return;
    } 

    onConfirm(addData);
    clearAddData();
  };

  // 데이터 빈칸 체크
  const checkEmptyData = (data) => {
    const tempData = Array.isArray(data)? [...data]: [...[data]]; // 배열이 아닌 경우 배열로 변환 후 복제

    // 데이터가 없을 때
    if(tempData.length === 0) { 
      return true;
    }

    // 데이터가 있을 때
    const hasValidData = tempData.some(
      item => !item.sit_response || !item.supervisor || !item.supervisor_contact ||
              !item.manager || !item.manager_contact || !item.coop_department
    );
    return hasValidData;
  };

  // FireCreateRowModal 렌더링
  return (
    <ModalTemplate
      isOpen={isOpen}
      handleConfirm={addAction}
      handleCancel={() => {
        clearAddData();
        onCancel();
      }}
      title="추가하기"
      cancelText="취소"
      confirmText="추가"
    >
      <Box sx={{ display: "flex", flexDirection: 'column', flex: 1, width: '100%', height: '100%', overflow: 'hidden', gap: 3, py: 2}}>
        {/* 상황대응 */}
        <TextField
          label={'상황대응'}
          value={addData?.sit_response || ""}
          fullWidth
          multiline
          minRows={3}
          maxRows={3}
          onChange={(e) => {
            setAddData({
              ...addData,
              sit_response: e.target.value,
            });
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              backgroundColor: theme.palette.background.paper,
            },
          }}
        />
        {/* 책임자 */}
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            label={'책임자'}
            value={addData?.supervisor || ""}
            fullWidth
            onChange={(e) => {
              setAddData({
                ...addData,
                supervisor: e.target.value,
              });
            }}
          />
          <TextField
            label={'책임자 연락처'}
            value={addData?.supervisor_contact || ""}
            fullWidth
            onChange={(e) => {
              setAddData({
                ...addData,
                supervisor_contact: e.target.value,
              });
            }}
          />
        </Box>
        {/* 담당자 */}
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            label={'담당자'}
            value={addData?.manager || ""}
            fullWidth
            onChange={(e) => {
              setAddData({
                ...addData,
                manager: e.target.value,
              });
            }}
          />
          <TextField
            label={'담당자 연락처'}
            value={addData?.manager_contact || ""}
            fullWidth
            onChange={(e) => {
              setAddData({
                ...addData,
                manager_contact: e.target.value,
              });
            }}
          />
        </Box>
        {/** 협조 부서 */}
        <TextField
          label={'협조 부서'}
          value={addData?.coop_department || ""}
          fullWidth
          onChange={(e) => {
            setAddData({
              ...addData,
              coop_department: e.target.value,
            });
          }}
        />
        {/** 비고 */}
        <TextField
          label={'비고'}
          value={addData?.note || ""}
          fullWidth
          onChange={(e) => {
            setAddData({
              ...addData,
              note: e.target.value,
            });
          }}
        />
      </Box>

      {/* 유효성 검사 알람 */}
      <SnackbarAlert 
        open={validationAlert.isOpen}
        onClose={closeValidationAlert}
        message={validationAlert.message}
        severity={validationAlert.severity} // 'success' | 'error' | 'warning' | 'info'
        // anchorOrigin = {{vertical: "top", horizontal: "center"}}
        snackBarSX={{width: '80%'}}
      />  
    </ModalTemplate>
  );
}
// 화재 시나리오 테이블의 행(row) 수정 모달
const FireEditRowModal = ({ isOpen, originalRowData, onConfirm, onCancel }) => {
  const theme = useTheme();

  // 유효성 검사 알람 상태 관리
  const [validationAlert, setValidationAlert] = useState({
    isOpen: false,
    message: '',
    severity: '',
  });
  const openValidationAlert = (message, severity = 'error') => {
    setValidationAlert({
      ...validationAlert,
      isOpen: true,
      message: message,
      severity: severity,
    });
  };
  const closeValidationAlert = (event, reason) => {
    if (reason === 'clickaway') return;
    setValidationAlert({
      ...validationAlert,
      isOpen: false,
      message: '',
      severity: '',
    });
  };

  // 수정 행 데이터 관리
  const [editData, setEditData] = useState([]);
  const clearEditData = () => setEditData([]);
  useEffect(() =>{
    setEditData(originalRowData);
  },[originalRowData])

  // 행 수정 완료 액션
  const editAction = () => {
    if(checkEmptyData(editData)) {
      openValidationAlert(message.notAllowedEmpty);
      return;
    } 
    //console.log("수정된 데이터:", editData);
      
    onConfirm(editData);
    clearEditData();
  };

  // 데이터 빈칸 체크
  const checkEmptyData = (data) => {
    const tempData = Array.isArray(data)? [...data]: [...[data]]; // 배열이 아닌 경우 배열로 변환 후 복제

    // 데이터가 없을 때
    if(tempData.length === 0) { 
      return true;
    }

    // 데이터가 있을 때
    const hasValidData = tempData.some(
      item => !item.sit_response || !item.supervisor || !item.supervisor_contact ||
              !item.manager || !item.manager_contact || !item.coop_department
    );
    return hasValidData;
  };

  // FireEditRowModal 렌더링
  return (
    <ModalTemplate
      isOpen={isOpen}
      handleConfirm={editAction}
      handleCancel={() => {
        clearEditData();
        onCancel();
      }}
      title="수정하기"
      cancelText="취소"
      confirmText="수정"
    >
      <Box sx={{ display: "flex", flexDirection: 'column', flex: 1, width: '100%', height: '100%', overflow: 'hidden', gap: 3, py: 2}}>
        {/* 상황대응 */}
        <TextField
          label={'상황대응'}
          value={editData?.sit_response || ""}
          fullWidth
          multiline
          minRows={3}
          maxRows={3}
          onChange={(e) => {
            setEditData({
              ...editData,
              sit_response: e.target.value,
            });
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              backgroundColor: theme.palette.background.paper,
            },
          }}
        />
        {/* 책임자 */}
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            label={'책임자'}
            value={editData?.supervisor || ""}
            fullWidth
            onChange={(e) => {
              setEditData({
                ...editData,
                supervisor: e.target.value,
              });
            }}
          />
          <TextField
            label={'책임자 연락처'}
            value={editData?.supervisor_contact || ""}
            fullWidth
            onChange={(e) => {
              setEditData({
                ...editData,
                supervisor_contact: e.target.value,
              });
            }}
          />
        </Box>
        {/* 담당자 */}
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            label={'담당자'}
            value={editData?.manager || ""}
            fullWidth
            onChange={(e) => {
              setEditData({
                ...editData,
                manager: e.target.value,
              });
            }}
          />
          <TextField
            label={'담당자 연락처'}
            value={editData?.manager_contact || ""}
            fullWidth
            onChange={(e) => {
              setEditData({
                ...editData,
                manager_contact: e.target.value,
              });
            }}
          />
        </Box>
        {/** 협조 부서 */}
        <TextField
          label={'협조 부서'}
          value={editData?.coop_department || ""}
          fullWidth
          onChange={(e) => {
            setEditData({
              ...editData,
              coop_department: e.target.value,
            });
          }}
        />
        {/** 비고 */}
        <TextField
          label={'비고'}
          value={editData?.note || ""}
          fullWidth
          onChange={(e) => {
            setEditData({
              ...editData,
              note: e.target.value,
            });
          }}
        />
      </Box>

      {/* 유효성 검사 알람 */}
      <SnackbarAlert 
        open={validationAlert.isOpen}
        onClose={closeValidationAlert}
        message={validationAlert.message}
        severity={validationAlert.severity} // 'success' | 'error' | 'warning' | 'info'
        // anchorOrigin = {{vertical: "top", horizontal: "center"}}
        snackBarSX={{width: '80%'}}
      />  
    </ModalTemplate>
  );
};

// 안전사고 시나리오 컴포넌트
const WorkplaceAccidentScenario = () => {
  const theme = useTheme();
  const { group_code, project_code } = useParams();

  const SIT_CATEGORY = 2; // 상황카테고리 : 2(안전사고)
  const UPDATE_STATUS = Object.freeze({ // 수정 상태
    EDIT: 'edit',
    ROW_ORDERING: 'rowOrdering',
  });

  ////////////////////////
  // CRUD 요청
  ////////////////////////

  // 데이터 조회 요청
  const getData = useGetTableDataInQueryString(
    "request",
    "/getSafeWorkplaceAccidentScenario",
    group_code,
    project_code,
    { params: { sitCategory: SIT_CATEGORY } } 
  );
  // 데이터 생성 요청
  const postData = useCreateTableData( 
    "request",
    "/postSafeWorkplaceAccidentScenario",
    group_code,
    project_code
  );
  // 데이터 수정 요청
  const putData = usePutTableData( 
    "request",
    "/putSafeWorkplaceAccidentScenario",
    group_code,
    project_code
  );
  // 데이터 삭제 요청
  const deleteData = useDeleteTableDataNew(
    "request",
    "/deleteSafeWorkplaceAccidentScenario",
    group_code,
    project_code
  );

  const tempGetData = getData.data || [];

  // 데이터 업데이트 알람 상태 관리
  const [dataAlert, setDataAlert] = useState({
    isOpen: false,
    message: '',
    severity:'' // 'success' | 'error' | 'warning' | 'info'
  });
  const openDataAlert = (message, severity = 'success') => {
    setDataAlert({
      ...dataAlert,
      isOpen: true,
      message: message,
      severity: severity
    });
  };
  const closeDataAlert = () => {
    setDataAlert({
      ...dataAlert,
      isOpen: false,
      message: '',
      severity: '',
    });
  };

  // 테이블 참조
  const tableRef = useRef();
  const clearSelection = () => { tableRef.current?.clearSelection(); };
  const getSelectedRows = () => { return tableRef.current?.getSelectedRows(); };

  // 테이블의 상단 툴바 커스터마이징 관련 내용
  const [showSearch, setShowSearch] = useState(false); // 검색창 열림 여부
  const [globalFilter, setGlobalFilter] = useState(''); // 검색 텍스트 상태
  const customTableTopToolbar = (table) => {
    return(
      <Box width="100%" display="flex" justifyContent="space-between" alignItems="center" p={2}>
        {/* 왼쪽 영역 */}
        <Box display="flex" alignItems="center" gap={2}>
          {/* TODO: */}
        </Box>
        
        {/* 오른쪽 영역 - 기존 내부 액션도 원하면 여기에 직접 렌더링 */}
        <Box display="flex" alignItems="center" gap={2}>
          {/* 검색창 */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton onClick={() => setShowSearch((prev) => !prev)}>
              <SearchIcon />
            </IconButton>
        
            <Collapse in={showSearch} orientation="horizontal">
              <TextField
                size="small"
                placeholder="검색"
                value={globalFilter}
                onChange={(e) => {
                  setGlobalFilter(e.target.value);
                  table.setGlobalFilter(e.target.value);
                }}
                sx={{ minWidth: 200 }}
                variant="outlined"
                slots={{ input: OutlinedInput }}
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => {
                            setGlobalFilter('');
                            table.setGlobalFilter('');
                            setShowSearch(false); // 닫기
                          }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                    </InputAdornment>
                    ),
                  }
                }}
              />
            </Collapse>
          </Box>
          {/* 추가 버튼 */}
          <Buttons.Add
            onClick={() => openAddModal()}
            sx={{ padding: '12px 16px' }}
          />
          {/* 삭제 버튼 */}
          <Buttons.Delete 
            onClick={openDeleteRecordModal}
            disabled={table.getSelectedRowModel().rows.length === 0}
            sx={{ padding: '12px 16px' }}
          />    
        </Box>
      </Box>
    );
  };

  // 추가 모달 상태 관리
  const [addModal, setAddModal] = useState({ isOpen: false });
  const openAddModal = () =>  setAddModal({ isOpen: true });
  const closeAddModal = () => setAddModal({ isOpen: false });
  // DB 업데이트(추가 데이터)
  const updateAddData = async (addData) => {
    try {
      // 상황카테고리 추가
      addData.sit_category = SIT_CATEGORY;

      // 데이터베이스 업데이트
      const result = await postData.mutateAsync(addData);
      if(result.status === 200) {
        openDataAlert(message.addSuccess);
      }

      // 생성 모달 초기화
      closeAddModal();
    }
    catch (error) {
      console.error("데이터 추가 중 오류 발생:", error);
    }
  };

  // 수정 모달 상태 관리
  const [editModal, setEditModal] = useState({ isOpen: false, rowData: [] });
  const openEditModal = (rowData) => setEditModal({ isOpen: true, rowData: rowData });
  const closeEditModal = () => setEditModal({ isOpen: false, rowData: []});
  // 테이블 행 수정 액션
  const customRenderRowActionContent = (rowData) => {
    return(
      <Buttons.Edit
        onClick={(event) =>{
          event.stopPropagation(); // 이벤트 전파 중단
          openEditModal(rowData);
        }}
        sx={{ padding: '8px 12px' }}
      />
    );
  };
  // DB 업데이트 (수정 데이터)
  const updateEditData = async (updateData) => {
    try {
      const data ={
        updateData: updateData,
        updateStatus: UPDATE_STATUS.EDIT, 
      };
      const result = await putData.mutateAsync(data);
      if(result.status === 200) {
        openDataAlert(message.updateEditSuccess);
      }

      // 수정 모달 초기화
      closeEditModal();
    }
    catch (error) {
      console.error("데이터 수정 중 오류 발생:", error);
    }
  }
  // DB 업데이트 (행 순서 변경)
  const updateRowOrderingData = async (draggedRowData, hoveredRowData) =>{
    try {
      //console.log('드래그한 row의 데이터:', draggedRowData);
      //console.log('호버된 row의 데이터:', hoveredRowData);

      const data = {
        updateData: {
          dragged_id: draggedRowData.id,
          dragged_sit_index: draggedRowData.sit_index,
          hovered_id: hoveredRowData.id,
          hovered_sit_index: hoveredRowData.sit_index,
          sit_category: SIT_CATEGORY,
        },
        updateStatus: UPDATE_STATUS.ROW_ORDERING,
      };

      // 데이터베이스 업데이트
      const result = await putData.mutateAsync(data);
      if(result.status) {
        openDataAlert(message.updateEditSuccess);
      }
    }
    catch (error) {
      console.error("데이터 수정 중 오류 발생:", error);
    }
  };

    // 레코드 삭제 모달 상태 관리
  const [deleteRecordModal, setDeleteRecordModal] = useState({ isOpen: false });
  const openDeleteRecordModal = () => setDeleteRecordModal({ isOpen: true });
  const closeDeleteRecordModal = () => setDeleteRecordModal({ isOpen: false });
  // DB 업데이트 (레코드 데이터 삭제) 
  const deleteRecordData = async () => {
    closeDeleteRecordModal();
    //console.log('✅ 확인 눌림 → 다음 동작 실행');
    try {

      // 선택된 행(row) 가져오기
      const selectedRows = getSelectedRows().map((row) => row.original);

      // 데이터베이스 업데이트
      const result = await deleteData.mutateAsync({
        params: {},
        body: {
          deleteData: selectedRows,
          sitCategory: SIT_CATEGORY,
        },
      });

      if(result.status === 200) {
        openDataAlert(message.deleteSuccess);
      }

      // 선택 상태 초기화
      clearSelection();
    }
    catch (error) {
      console.error("데이터 삭제 중 오류 발생:", error);
    }
  };

  // WorkplaceAccidentScenario 렌더링
  return (
    <TableContainer>
      {/** 테이블 표시 */}
      <NewTableTemplate
        ref={tableRef}
        columns={standardScenarioColumns}
        data={tempGetData}
        topToolbarContent={customTableTopToolbar}
        renderRowActionContent={customRenderRowActionContent}
        onRowOrdering={updateRowOrderingData}
        enableCustomGlobalFilter={true}
        enableRowOrdering={true}
        templateSX ={{
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: "0.375rem", 
          boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.05)',
        }}
      />
      {/* 행(row) 추가 모달 */}
      <WorkplaceAccidentCreateRowModal
        isOpen={addModal.isOpen}
        onConfirm={updateAddData}
        onCancel={closeAddModal}
      />
      {/* 행(row) 수정 모달 */}
      <WorkplaceAccidentEditRowModal
        isOpen={editModal.isOpen}
        originalRowData={editModal.rowData}
        onConfirm={updateEditData}
        onCancel={closeEditModal}
      />
      {/** 레코드 삭제 메시지 */}
      <ConfirmDialog
        open={deleteRecordModal.isOpen}
        title="정말 삭제하시겠어요?"
        message="삭제하면 복구할 수 없습니다."
        onConfirm={deleteRecordData}
        onCancel={closeDeleteRecordModal}
      />
      {/* 데이터 알람 */}
      <SnackbarAlert 
        open={dataAlert.isOpen}
        onClose={closeDataAlert}
        message={dataAlert.message}
        severity={dataAlert.severity} // 'success' | 'error' | 'warning' | 'info'
        //anchorOrigin = {{vertical: "top", horizontal: "center"}}
      />
    </TableContainer>
  );
};
// 안전사고 시나리오 테이블의 행(row) 추가 모달
const WorkplaceAccidentCreateRowModal = ({ isOpen, onConfirm, onCancel }) => {
  const theme = useTheme();

  // 유효성 검사 알람 상태 관리
  const [validationAlert, setValidationAlert] = useState({
    isOpen: false,
    message: '',
    severity: '',
  });
  const openValidationAlert = (message, severity = 'error') => {
    setValidationAlert({
      ...validationAlert,
      isOpen: true,
      message: message,
      severity: severity,
    });
  };
  const closeValidationAlert = (event, reason) => {
    if (reason === 'clickaway') return;
    setValidationAlert({
      ...validationAlert,
      isOpen: false,
      message: '',
      severity: '',
    });
  };

  // 추가 행 데이터 관리
  const [addData, setAddData] = useState([]);
  const clearAddData = () => setAddData([]);
  // 행 추가 완료 액션
  const addAction = () => {
    if(checkEmptyData(addData)) {
      openValidationAlert(message.notAllowedEmpty);
      return;
    } 

    onConfirm(addData);
    clearAddData();
  };

  // 데이터 빈칸 체크
  const checkEmptyData = (data) => {
    const tempData = Array.isArray(data)? [...data]: [...[data]]; // 배열이 아닌 경우 배열로 변환 후 복제

    // 데이터가 없을 때
    if(tempData.length === 0) { 
      return true;
    }

    // 데이터가 있을 때
    const hasValidData = tempData.some(
      item => !item.sit_response || !item.supervisor || !item.supervisor_contact ||
              !item.manager || !item.manager_contact || !item.coop_department
    );
    return hasValidData;
  };

  // WorkplaceAccidentCreateRowModal 렌더링
  return (
    <ModalTemplate
      isOpen={isOpen}
      handleConfirm={addAction}
      handleCancel={() => {
        clearAddData();
        onCancel();
      }}
      title="추가하기"
      cancelText="취소"
      confirmText="추가"
    >
      <Box sx={{ display: "flex", flexDirection: 'column', flex: 1, width: '100%', height: '100%', overflow: 'hidden', gap: 3, py: 2}}>
        {/* 상황대응 */}
        <TextField
          label={'상황대응'}
          value={addData?.sit_response || ""}
          fullWidth
          multiline
          minRows={3}
          maxRows={3}
          onChange={(e) => {
            setAddData({
              ...addData,
              sit_response: e.target.value,
            });
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              backgroundColor: theme.palette.background.paper,
            },
          }}
        />
        {/* 책임자 */}
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            label={'책임자'}
            value={addData?.supervisor || ""}
            fullWidth
            onChange={(e) => {
              setAddData({
                ...addData,
                supervisor: e.target.value,
              });
            }}
          />
          <TextField
            label={'책임자 연락처'}
            value={addData?.supervisor_contact || ""}
            fullWidth
            onChange={(e) => {
              setAddData({
                ...addData,
                supervisor_contact: e.target.value,
              });
            }}
          />
        </Box>
        {/* 담당자 */}
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            label={'담당자'}
            value={addData?.manager || ""}
            fullWidth
            onChange={(e) => {
              setAddData({
                ...addData,
                manager: e.target.value,
              });
            }}
          />
          <TextField
            label={'담당자 연락처'}
            value={addData?.manager_contact || ""}
            fullWidth
            onChange={(e) => {
              setAddData({
                ...addData,
                manager_contact: e.target.value,
              });
            }}
          />
        </Box>
        {/** 협조 부서 */}
        <TextField
          label={'협조 부서'}
          value={addData?.coop_department || ""}
          fullWidth
          onChange={(e) => {
            setAddData({
              ...addData,
              coop_department: e.target.value,
            });
          }}
        />
        {/** 비고 */}
        <TextField
          label={'비고'}
          value={addData?.note || ""}
          fullWidth
          onChange={(e) => {
            setAddData({
              ...addData,
              note: e.target.value,
            });
          }}
        />
      </Box>

      {/* 유효성 검사 알람 */}
      <SnackbarAlert 
        open={validationAlert.isOpen}
        onClose={closeValidationAlert}
        message={validationAlert.message}
        severity={validationAlert.severity} // 'success' | 'error' | 'warning' | 'info'
        // anchorOrigin = {{vertical: "top", horizontal: "center"}}
        snackBarSX={{width: '80%'}}
      />  
    </ModalTemplate>
  );
};
// 안전사고 시나리오 테이블의 행(row) 수정 모달
const WorkplaceAccidentEditRowModal = ({ isOpen, originalRowData, onConfirm, onCancel }) => {
  const theme = useTheme();

  // 유효성 검사 알람 상태 관리
  const [validationAlert, setValidationAlert] = useState({
    isOpen: false,
    message: '',
    severity: '',
  });
  const openValidationAlert = (message, severity = 'error') => {
    setValidationAlert({
      ...validationAlert,
      isOpen: true,
      message: message,
      severity: severity,
    });
  };
  const closeValidationAlert = (event, reason) => {
    if (reason === 'clickaway') return;
    setValidationAlert({
      ...validationAlert,
      isOpen: false,
      message: '',
      severity: '',
    });
  };

  // 수정 행 데이터 관리
  const [editData, setEditData] = useState([]);
  const clearEditData = () => setEditData([]);
  useEffect(() =>{
    setEditData(originalRowData);
  },[originalRowData])

  // 행 수정 완료 액션
  const editAction = () => {
    if(checkEmptyData(editData)) {
      openValidationAlert(message.notAllowedEmpty);
      return;
    } 
    //console.log("수정된 데이터:", editData);
      
    onConfirm(editData);
    clearEditData();
  };

  // 데이터 빈칸 체크
  const checkEmptyData = (data) => {
    const tempData = Array.isArray(data)? [...data]: [...[data]]; // 배열이 아닌 경우 배열로 변환 후 복제

    // 데이터가 없을 때
    if(tempData.length === 0) { 
      return true;
    }

    // 데이터가 있을 때
    const hasValidData = tempData.some(
      item => !item.sit_response || !item.supervisor || !item.supervisor_contact ||
              !item.manager || !item.manager_contact || !item.coop_department
    );
    return hasValidData;
  };

  // WorkplaceAccidentEditRowModal 렌더링
  return (
    <ModalTemplate
      isOpen={isOpen}
      handleConfirm={editAction}
      handleCancel={() => {
        clearEditData();
        onCancel();
      }}
      title="수정하기"
      cancelText="취소"
      confirmText="수정"
    >
      <Box sx={{ display: "flex", flexDirection: 'column', flex: 1, width: '100%', height: '100%', overflow: 'hidden', gap: 3, py: 2}}>
        {/* 상황대응 */}
        <TextField
          label={'상황대응'}
          value={editData?.sit_response || ""}
          fullWidth
          multiline
          minRows={3}
          maxRows={3}
          onChange={(e) => {
            setEditData({
              ...editData,
              sit_response: e.target.value,
            });
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              backgroundColor: theme.palette.background.paper,
            },
          }}
        />
        {/* 책임자 */}
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            label={'책임자'}
            value={editData?.supervisor || ""}
            fullWidth
            onChange={(e) => {
              setEditData({
                ...editData,
                supervisor: e.target.value,
              });
            }}
          />
          <TextField
            label={'책임자 연락처'}
            value={editData?.supervisor_contact || ""}
            fullWidth
            onChange={(e) => {
              setEditData({
                ...editData,
                supervisor_contact: e.target.value,
              });
            }}
          />
        </Box>
        {/* 담당자 */}
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            label={'담당자'}
            value={editData?.manager || ""}
            fullWidth
            onChange={(e) => {
              setEditData({
                ...editData,
                manager: e.target.value,
              });
            }}
          />
          <TextField
            label={'담당자 연락처'}
            value={editData?.manager_contact || ""}
            fullWidth
            onChange={(e) => {
              setEditData({
                ...editData,
                manager_contact: e.target.value,
              });
            }}
          />
        </Box>
        {/** 협조 부서 */}
        <TextField
          label={'협조 부서'}
          value={editData?.coop_department || ""}
          fullWidth
          onChange={(e) => {
            setEditData({
              ...editData,
              coop_department: e.target.value,
            });
          }}
        />
        {/** 비고 */}
        <TextField
          label={'비고'}
          value={editData?.note || ""}
          fullWidth
          onChange={(e) => {
            setEditData({
              ...editData,
              note: e.target.value,
            });
          }}
        />
      </Box>

      {/* 유효성 검사 알람 */}
      <SnackbarAlert 
        open={validationAlert.isOpen}
        onClose={closeValidationAlert}
        message={validationAlert.message}
        severity={validationAlert.severity} // 'success' | 'error' | 'warning' | 'info'
        // anchorOrigin = {{vertical: "top", horizontal: "center"}}
        snackBarSX={{width: '80%'}}
      />  
    </ModalTemplate>
  );
};